import { defineStore } from 'pinia'
import { ref } from 'vue'
import i18n, { SUPPORTED_LOCALES, isSupportedLocale, type SupportedLocale } from '../i18n'
import { api } from '../lib/api'

const STORAGE_KEY = 'to_locale'

function setI18nLocale(locale: SupportedLocale) {
  i18n.global.locale.value = locale
  document.documentElement.setAttribute('lang', locale)
}

function detectBrowserLocale(): SupportedLocale | null {
  if (typeof navigator === 'undefined') return null
  const candidates = [navigator.language, ...(navigator.languages ?? [])]
  for (const lang of candidates) {
    if (!lang) continue
    const short = lang.slice(0, 2).toLowerCase()
    if (isSupportedLocale(short)) return short
  }
  return null
}

export const useLocaleStore = defineStore('locale', () => {
  const current = ref<SupportedLocale>('en')

  function hydrate() {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (isSupportedLocale(stored)) {
      current.value = stored
      setI18nLocale(stored)
      return
    }
    const browser = detectBrowserLocale()
    if (browser) {
      current.value = browser
      setI18nLocale(browser)
      return
    }
    setI18nLocale('en')
  }

  /** Apply the value stored on the user's profile after login. Does not persist back. */
  function syncFromProfile(locale: string | null | undefined) {
    if (!isSupportedLocale(locale)) return
    if (locale === current.value) return
    current.value = locale
    setI18nLocale(locale)
    localStorage.setItem(STORAGE_KEY, locale)
  }

  async function setLocale(locale: SupportedLocale, opts: { persistRemote?: boolean } = {}) {
    if (!isSupportedLocale(locale)) return
    current.value = locale
    setI18nLocale(locale)
    localStorage.setItem(STORAGE_KEY, locale)

    if (opts.persistRemote ?? true) {
      try {
        await api.updateProfile({ preferred_language: locale })
      } catch {
        // Unauthenticated or profile not yet created — localStorage is enough.
      }
    }
  }

  return {
    current,
    supported: SUPPORTED_LOCALES,
    hydrate,
    syncFromProfile,
    setLocale,
  }
})
